'use client';

/* oxlint-disable react/button-has-type */
// https://github.com/yannickcr/eslint-plugin-react/issues/1555
import { ButtonHTMLAttributes, MouseEventHandler, ReactNode, Ref, useCallback, useState } from 'react';
import cx from 'classnames';

import { Spinner } from '../Spinner';
import { SizeType } from '../types/base';
import styles from './button.module.scss';

export type ShapeType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'link1'
  | 'link2'
  | 'link3'
  | 'combi1'
  | 'combi2'
  | 'circle1'
  | 'circle2'
  | 'trans'
  | 'long'
  | 'short';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'translate' | 'prefix'> {
  size?: SizeType;
  shape?: ShapeType;
  className?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  btnRef?: Ref<HTMLButtonElement | null>;
  children: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  underline?: boolean;
  isSubmit?: boolean;
  loading?: boolean;
  isFull?: boolean;
  withSpinner?: boolean;
}

export const Button = (props: Props) => {
  const {
    size = 'large',
    shape = 'primary',
    className,
    onClick,
    btnRef,
    children,
    prefix,
    suffix,
    underline,
    disabled,
    isSubmit = false,
    loading,
    isFull,
    withSpinner = true,
    ...restProps
  } = props;

  const [waiting, setWaiting] = useState(false);

  const buttonLoading = withSpinner && (loading || waiting);
  const buttonDisabled = disabled || loading || waiting;

  const buttonOnClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      try {
        setWaiting(true);
        // oxlint-disable-next-line @typescript-eslint/await-thenable
        await onClick?.(e);
      } finally {
        setWaiting(false);
      }
    },
    [onClick],
  );

  return (
    <button
      type={isSubmit ? 'submit' : 'button'}
      onClick={buttonOnClick}
      disabled={buttonDisabled}
      className={cx(styles.wrapper, styles[size], styles[shape], className, {
        [styles.underline]: underline,
        [styles.isFull]: isFull,
        [styles.isLoading]: buttonLoading,
      })}
      ref={btnRef}
      {...restProps}
    >
      {prefix}
      {children}
      {suffix}
      {buttonLoading && <Spinner size='tiny' className={styles.spinner} />}
    </button>
  );
};
